import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import moment from 'moment';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import intersection from 'lodash/intersection';
import isEqual from 'lodash/isEqual';

import { ajaxFetch, buildURLParamsString, getURLParams } from '~/helpers/api';
import { defaultUserContextWithFlags, UserContext } from '~/dashboard/context';
import { routes } from '~/helpers/routes';


export function useURLParamState(key, defaultValue = '') {
  const urlParams = getURLParams();

  // Track the state for the input parameter
  const [value, setValue] = useState(urlParams[key] || defaultValue);
  const handleSelectValue = updatedValue => {
    if (updatedValue !== value) {
      const url = window.location.pathname + buildURLParamsString({ [key]: updatedValue });
      history.pushState({ [key]: updatedValue }, '', url);
      setValue(updatedValue);
    }
  };

  useEffect(() => {
    if (!urlParams.tab) {
      const url = window.location.pathname + buildURLParamsString({ [key]: value });
      history.replaceState({ [key]: value }, '', url);
    }

    window.onpopstate = event => { setValue(event.state[key]); };
  });

  return [value, handleSelectValue];
}

/**
 * Contains the logic for fetching the user context from the backend.
 * @returns a context of type UserContext. Will be the default (empty) UserContext at first,
 *   then will update with a real UserContext fetched from the Daffy server. Watch its
 *   state to receive a real user context.
 */
export function useFetchedUserContext() {
  const [context, setContext] = useState(defaultUserContextWithFlags);
  const getContext = useCallback(async () => {
    const contextData = await ajaxFetch(routes.v3.userContext);
    setContext({ ...contextData });
  }, [setContext]);
  useEffect(() => { getContext(); }, [getContext]);
  return context;
}

export function useFetchedUserContextWithIndicator() {
  const [userContext, setUserContext] = useState({
    ...defaultUserContextWithFlags,
    fetchResolved: false,
  });
  useEffect(() => {
    const fetchUserContext = async () => {
      const contextData = await ajaxFetch(routes.v3.userContext);
      setUserContext({
        ...contextData,
        fetchResolved: true,
      });
    };
    fetchUserContext();
  }, []);
  return userContext;
}

/*
  NOTE: This could potentially be a dedicated util instead of a hook, but it is built off of an exisiting hook,
        which is why it is currently here. We are introducing permissions into CaseManagement, and this function
        is meant to track whether or not case(s) are actionable. This is used for both multi-select and single
        cases, which is why they are tracked using an object
*/
export function useCaseMgmtPermissions(cases, isBulkAction) {
  const actionabilityByToken = {};
  const [tagTypesByName, setTagTypesByName] = useState(defaultUserContextWithFlags);
  const { agent, permissions } = useFetchedUserContext();

  const getTagTypesByName = useCallback(async () => {
    const tagsTypesUrl = `${window.location.origin}${routes.v3.settings.tagTypes}`;
    const responseData = await ajaxFetch(tagsTypesUrl);
    const processedTagTypes = {};
    responseData.data.forEach(tagType => {
      processedTagTypes[tagType.name] = tagType.customer_tag_type_id;
    });
    setTagTypesByName(processedTagTypes);
  }, [setTagTypesByName]);
  useEffect(() => { getTagTypesByName(); }, [getTagTypesByName]);

  const hasCaseUpdatePermissions = permissions?.['CREATE:CaseWorks:null'];
  const unrestrictedPermissions = permissions ? (!hasCaseUpdatePermissions?.customer_tag_type_id
    || !hasCaseUpdatePermissions?.customer_tag_type_id.length) : false;
  cases.forEach(caseData => {
    const assignedAgentEmail = caseData?.assigned_agent_email
      || caseData?.assignedAgentEmail || null;
    const caseToken = caseData?.case_token || caseData?.caseToken || null;
    const closed = caseData?.closed || null;

    // by default, closed cases are not actionable by anyone. However adding notes ignores this permission and so is always possible.
    if (!closed) {
      if (agent.is_employee) actionabilityByToken[caseToken] = true;
      else if (agent.is_agent) {
        if (hasCaseUpdatePermissions) {
          if (unrestrictedPermissions) {
            actionabilityByToken[caseToken] = true;
          } else {
            const validCaseTypeId = tagTypesByName[caseData.customer_tag_type_name];
            const restrictedPermArray = hasCaseUpdatePermissions?.customer_tag_type_id;
            const hasRestrictedPerm = restrictedPermArray.find(id => +id === +validCaseTypeId);
            if (hasRestrictedPerm) actionabilityByToken[caseToken] = true;
          }
        } else if (!assignedAgentEmail && isBulkAction) actionabilityByToken[caseToken] = true;
        else if (assignedAgentEmail === agent.email) actionabilityByToken[caseToken] = true;
      }
    }
  });
  if (!isBulkAction) {
    return Boolean(Object.keys(actionabilityByToken).length);
  }
  return actionabilityByToken;
}

export function usePermissions() {
  let initialPermissions = {};
  const context = useContext(UserContext);
  const jsonPermsInput = document.getElementById('json-perms');
  if (jsonPermsInput) {
    initialPermissions = JSON.parse(jsonPermsInput.value);
  } else {
    // When page is not built off of base, permissions are included in context
    initialPermissions = context.permissions;
  }
  /*
    NOTE: We are not managing the returned permissions object with useState() because we set the state as
    the initial context permissions, which might be an empty object. but we never update the state when
    the context is updated from a backend request. We have observed using useState() here may actually
    return an empty object rather than the actual permissions in scope of this function.
  */
  return [initialPermissions];
}

export function useServiceResponses() {
  const [state, setState] = useState({ selectedServiceResponse: 0, serviceResponses: {} });

  const updateSelectedServiceResponse = useCallback((selectedServiceResponse, url = false) => {
    const serviceResponses = { ...state.serviceResponses };
    const newState = { ...state, selectedServiceResponse };
    setState(newState);

    if (typeof selectedServiceResponse === 'number') {
      if (selectedServiceResponse && !serviceResponses[selectedServiceResponse] && url) {
        ajaxFetch(url).then(data => {
          serviceResponses[selectedServiceResponse] = data.service_response;
          setState({ ...newState, serviceResponses });
        });
      }
    }
  }, [state]);

  return [state.selectedServiceResponse, state.serviceResponses, updateSelectedServiceResponse];
}

// Use previous props or state
export function usePrevious(value) {
  const ref = useRef();
  useEffect(() => { ref.current = value; });
  return ref.current;
}

export function useTimedState(initialState, timeToExpire) {
  const [state, setState] = useState({ ...initialState });

  useEffect(() => {
    if (!isEqual(state, initialState)) setTimeout(() => { setState(initialState); }, timeToExpire);
  }, [state, initialState, timeToExpire]);
}


export function useMessages() {
  // Messages get flashed in order, so we can use array for storage.
  const [messages, setMessages] = useState([]);
  // If currently displayed message has expiration, record time displayed to calculate
  // relative time until expiration
  const [displayedAt, setDisplayedAt] = useState(null);

  // Add message to end of existing array
  const createMessage = useCallback((text, type = 'info', expireIn = 5000) => {
    const textArray = Array.isArray(text) ? text : [text];
    textArray.forEach(textString => {
      setMessages(messageArray => [...messageArray, { text: textString, type, expireIn }]);
    });
  }, []);

  // Delete persistent message, using passed id
  const deleteMessage = useCallback(() => {
    setMessages(messageArray => messageArray.slice(1));
    setDisplayedAt(null);
  }, []);

  // Side effect to expire flash messages after they get created
  useEffect(() => {
    if (messages.length && messages[0].expireIn && !displayedAt) {
      setDisplayedAt(moment.utc());
      setTimeout(() => deleteMessage(), messages[0].expireIn);
    }
  }, [messages, displayedAt, deleteMessage]);

  return {
    messages, displayedAt, createMessage, deleteMessage,
  };
}

const convertToLegacyParams = obj => {
  const filterBy = {};
  const searchBy = {};
  Object.keys(obj).forEach(key => {
    const rule = obj[key];
    if (Array.isArray(rule)) {
      const [, operator, value] = rule;
      if (operator === 'like') {
        searchBy[key] = value;
      } else if (operator === 'in') {
        filterBy[key] = { should: value };
      } else {
        filterBy[key] = value;
      }
    } else if (typeof rule === 'object' && rule.and) {
      filterBy[key] = { filter: rule.and.map(([,, value]) => value) };
    }
  });
  return { filterBy, searchBy };
};

export const convertFromLegacyParams = ({ filterBy, searchBy }) => {
  const obj = {};
  Object.keys(filterBy).forEach(key => {
    if (typeof filterBy[key] === 'object') {
      if (Array.isArray(filterBy[key])) {
        // Timestamp
        obj[key] = [key, 'between', filterBy[key]];
      } else if (filterBy[key].should) {
        // Multi-value "or" filter
        obj[key] = [key, 'in', filterBy[key].should];
      } else if (filterBy[key].filter) {
        // Multi-value "and" filter
        obj[key] = { and: filterBy[key].filter.map(val => [key, 'is', val]) };
      }
    } else {
      // Single value filter
      obj[key] = [key, 'is', filterBy[key]];
    }
  });
  Object.keys(searchBy).forEach(key => { obj[key] = [key, 'like', searchBy[key]]; });
  return obj;
};

export function useFilteredData({
  items,
  initialFilterBy = {},
  facets = [],
  usedFilters = [],
}) {
  const [filterBy, setFilterBy] = useState(initialFilterBy);

  // Logic for evaluating an item against a specific filterBy rule
  const evaluateRule = useCallback((item, rule) => {
    const [field, operator, filterValue] = rule;
    if (usedFilters.length && !usedFilters.includes(field)) return true;
    const value = get(item, field);
    const valueIsArray = Array.isArray(value);
    switch (operator) {
      case 'is': {
        if (valueIsArray) {
          return value.includes(filterValue);
        }
        return value === filterValue;
      }
      case 'like': {
        return value && value.toLowerCase().includes(filterValue.toLowerCase());
      }
      case 'in': {
        let processedValue = value;
        if (value === null && filterValue.includes('null')) {
          processedValue = 'null';
        } else if (valueIsArray && value.includes(null) && filterValue.includes('null')) {
          processedValue = ['null'];
        }
        return processedValue && (
          valueIsArray
            ? intersection(processedValue, filterValue).length
            : filterValue.includes(processedValue)
        );
      }
      case 'between': {
        if (filterValue[1] === 'now') {
          return moment(value).isAfter(moment().subtract(...filterValue[0]));
        }
        return moment(value).isBetween(moment(filterValue[0]), moment(filterValue[1]));
      }
      case 'function': {
        const facet = facets.find(f => f.key === field);
        const allOptions = [...facet.options, facet.dropdown_label];
        const facetValue = allOptions.find(o => o.value === filterValue)
          || allOptions.find(o => o.hidden);
        return facetValue.booleanFilter(value);
      }
      default: {
        return false;
      }
    }
  }, [facets, usedFilters]);

  const evaluateGroup = useCallback((item, group) => {
    if (group.and) {
      return group.and.every(rule => evaluateRule(item, rule));
    }
    if (group.or) {
      return group.and.some(rule => evaluateRule(item, rule));
    }
    return false;
  }, [evaluateRule]);

  // Filter items based on the filterBy state rules
  const filteredItems = useMemo(() => {
    let filtered = [...items];
    const filterKeys = Object.keys(filterBy);
    filterKeys.forEach(key => {
      filtered = filtered.filter(item => {
        if (Array.isArray(filterBy[key])) return evaluateRule(item, filterBy[key]);
        if (filterBy[key].and || filterBy[key].or) return evaluateGroup(item, filterBy[key]);
        return false;
      });
    });
    return filtered;
  }, [evaluateRule, evaluateGroup, filterBy, items]);

  // Updates the table filterBy param given a key and value
  const updateFilterByClosure = useCallback((operator, updatePage) => (key, value) => {
    setFilterBy(oldFilterBy => {
      const updatedFilterBy = cloneDeep(oldFilterBy);
      const facet = facets.find(f => f.key === key);

      if (
        (facet.dropdown_label && value === facet.dropdown_label.value)
        || (facet.filter_type === 'timestamp' && value === null)
      ) {
        // Selected the default option, delete the filterBy key
        delete updatedFilterBy[key];
      } else {
        updatedFilterBy[key] = [key, operator, value];
      }
      if (updatePage) {
        updatePage(0);
      }
      return updatedFilterBy;
    });
  }, [facets]);

  const clearAllFilters = useCallback(() => {
    setFilterBy(initialFilterBy);
  }, [initialFilterBy]);


  return {
    convertToLegacyParams,
    convertFromLegacyParams,
    filterBy,
    items: filteredItems,
    setFilterBy,
    updateFilterByClosure,
    clearAllFilters,
  };
}

// this alphanumeric sort leaves nulls at the end of the list:
export function alphanumericSort(sortableItems, sortBy) {
  const [[key, direction]] = sortBy;
  return sortableItems.sort((a, b) => {
    let primaryValue = get(a, key);
    let secondaryValue = get(b, key);
    // preprocess data/normalize string values so casing doesn't affect sorting
    if (typeof primaryValue === 'string') {
      primaryValue = primaryValue.toLowerCase();
    }
    if (typeof secondaryValue === 'string') {
      secondaryValue = secondaryValue.toLowerCase();
    }
    // actually sort values
    if (primaryValue === secondaryValue) {
      return 0;
    }
    if (primaryValue === null) {
      return 1;
    }
    if (secondaryValue === null) {
      return -1;
    }
    if (direction === 'ASC') {
      return primaryValue < secondaryValue ? -1 : 1;
    }
    return primaryValue < secondaryValue ? 1 : -1;
  });
}

export function useSortedData(items = [], defaultSortBy = [[]]) {
  const [sortBy, setSortBy] = useState(defaultSortBy);

  const sortedItems = useMemo(() => {
    let sortableItems = items ? [...items] : [];
    if (sortBy.length && sortBy[0].length) {
      sortableItems = alphanumericSort(sortableItems, sortBy);
    }
    return sortableItems;
  }, [items, sortBy]);

  const triggerSort = key => {
    if (sortBy.length && key) {
      const [[sortKey, sortDirection]] = sortBy;
      let direction = 'ASC';
      if (sortKey === key && sortDirection === 'ASC') {
        direction = 'DESC';
      }
      setSortBy([[key, direction]]);
    } else {
      setSortBy([[]]);
    }
  };

  const getSortedClassName = (key = null) => {
    if (key === null) return '';
    const defaultClassName = 'sortable';
    if (!sortBy.length || !sortBy[0].length) {
      return defaultClassName;
    }
    return `${defaultClassName} ${sortBy[0][0] === key ? sortBy[0][1] : ''}`;
  };

  return {
    items: sortedItems, triggerSort, sortBy, getSortedClassName,
  };
}


export function usePaginatedData(items, config = {}) {
  const [currentPage, setCurrentPage] = useState(0);
  const updatePage = page => setCurrentPage(page);

  // Calculate number of pages from pageSize, array length
  const pageSize = config.pageSize || 20;
  const totalPages = Math.ceil(items.length / pageSize);

  // Slice the full array of items into page subset
  const startIndex = currentPage * pageSize;
  const pageItems = items.slice(startIndex, startIndex + pageSize);

  return {
    currentPage,
    pageItems,
    pageSize,
    updatePage,
    totalPages,
  };
}

export function useDelayedRender(delay) {
  const [delayed, setDelayed] = useState(true);
  useEffect(() => {
    const timeout = setTimeout(() => setDelayed(false), delay);
    return () => clearTimeout(timeout);
  }, [delay]);
  return fn => !delayed && fn();
}

// custom interval hook suggested & created by Dan Abramov
// https://overreacted.io/making-setinterval-declarative-with-react-hooks/
export function useInterval(callback, delay) {
  const savedCallback = useRef();

  // Remember the latest function.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

export const getElementDimensions = node => {
  const computedStyle = window.getComputedStyle(node);

  let width = node.clientWidth;
  let height = node.clientHeight;

  height -= parseFloat(computedStyle.paddingTop) + parseFloat(computedStyle.paddingBottom);
  width -= parseFloat(computedStyle.paddingLeft) + parseFloat(computedStyle.paddingRight);
  return { height, width };
};

export function useElementSize(elementClass = undefined) {
  const [size, setSize] = useState({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    const element = document.querySelector(elementClass);
    const onResize = () => {
      let sizeObject;

      if (elementClass && element) {
        sizeObject = {
          width: getElementDimensions(element).width,
          height: getElementDimensions(element).height,
        };
      } else {
        sizeObject = {
          width: window.innerWidth,
          height: window.innerHeight,
        };
      }

      setTimeout(() => setSize(sizeObject), 0);
    };

    window.addEventListener('resize', onResize);
    new ResizeObserver(onResize).observe(element);
    onResize();

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [elementClass]);

  return size;
}

export function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;

  return {
    width,
    height,
  };
}

/**
 * Hook to centralize evidence permission logic.
 *
 * Permission Rules:
 * - CREATE permission implies READ permission (users who can create can also read)
 * - READ-only users can view evidence but cannot add/upload
 * - CREATE users see "Add Evidence" text/buttons
 * - READ-only users see "View Evidence" text/buttons
 * - If permissions are off, no evidence features are visible
 * - Alloy admins (isEmployee) cannot have CREATE permissions (security restriction)
 */
export function useEvidencePermissions() {
  const { agent, permissions } = useContext(UserContext);

  // Alloy admins (employees) should not be able to create evidence
  const isAlloyAdmin = !!agent?.isEmployee;

  // Users with CREATE permission can also read evidence (but only if feature flag is on)
  const canReadEvidence = !!permissions?.['READ:Evidence:null']
    || !!permissions?.['CREATE:Evidence:null'];

  // CREATE permission is disabled for Alloy admins (and requires feature flag)
  const canCreateEvidence = !isAlloyAdmin
    && !!permissions?.['CREATE:Evidence:null'];

  // Button/menu text changes based on permission level
  const evidenceButtonText = canCreateEvidence ? 'Add Evidence' : 'View Evidence';

  return {
    canReadEvidence,
    canCreateEvidence,
    evidenceButtonText,
  };
}
